import React, { useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  Box,
  Heading,
  HStack,
  FormControl,
  FormLabel,
  Select,
  Text,
} from "@chakra-ui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChartBar } from "@fortawesome/free-solid-svg-icons";
import { StandardPageTitle, StandardHeader } from "../../themes/StandardHeader";

// สีสำหรับวัคซีนแต่ละชนิด
const COLORS = [
  "#0088FE",
  "#00C49F",
  "#FFBB28",
  "#FF8042",
  "#A28EFF",
  "#FF6F61",
  "#6B7280",
];

const VaccineProductionChart = ({ data, livestockTypeOptions }) => {
  const [selectedLivestockType, setSelectedLivestockType] = useState("");

  // จัดกลุ่มข้อมูลตามเดือนและวัคซีน
  const prepareChartData = () => {
    const groupedData = {};

    // กรองข้อมูลตามประเภทปศุสัตว์
    const filteredData = selectedLivestockType
      ? data.filter((item) => item.LivestockType === selectedLivestockType)
      : data;

    filteredData.forEach((item) => {
      // แปลง ProductionDate เป็น YYYY-MM
      const date = new Date(item.ProductionDate);
      const monthKey = `${date.getFullYear()}-${(date.getMonth() + 1)
        .toString()
        .padStart(2, "0")}`;

      if (!groupedData[monthKey]) {
        groupedData[monthKey] = { date: monthKey };
      }

      // เพิ่มปริมาณสำหรับวัคซีนนี้
      groupedData[monthKey][item.VaccineNameTH] =
        (groupedData[monthKey][item.VaccineNameTH] || 0) +
        Number(item.TotalQuantity);
    });

    // แปลงเป็น array สำหรับ recharts
    return Object.values(groupedData).sort((a, b) =>
      a.date.localeCompare(b.date)
    );
  };

  // หาวัคซีนทั้งหมดสำหรับ bars
  const vaccineTypes = [
    ...new Set(
      (selectedLivestockType
        ? data.filter((item) => item.LivestockType === selectedLivestockType)
        : data
      ).map((item) => item.VaccineNameTH)
    ),
  ].sort();

  // ข้อมูลสำหรับกราฟ
  const chartData = prepareChartData();

  return (
    <Box
      borderWidth={1}
      borderRadius="lg"
      boxShadow="lg"
      p={5}
      mb={6}
      bg="white"
    >
      <Box
        mb={5}
        borderRadius="full"
        w="100%"
        bgColor="iBlue.600"
        color="white"
        p={2}
      >
        <HStack spacing={5} ml={5}>
          <FontAwesomeIcon icon={faChartBar} color="white" size="xl" />
          <Heading size={{ base: "md", md: "lg" }} fontWeight="normal">
            กราฟปริมาณการผลิตวัคซีน (รายเดือน)
          </Heading>
        </HStack>
      </Box>
      <FormControl mb={4} maxW="300px">
        <FormLabel fontSize="xl" color="gray">
          ประเภทปศุสัตว์
        </FormLabel>
        {livestockTypeOptions.length === 0 ? (
          <Text color="gray.500">ไม่มีข้อมูลประเภทปศุสัตว์</Text>
        ) : (
          <Select
            value={selectedLivestockType}
            onChange={(e) => setSelectedLivestockType(e.target.value)}
            placeholder="เลือกประเภทปศุสัตว์ (ทั้งหมด)"
            colorScheme="iBlue"
          >
            {livestockTypeOptions.map((type) => (
              <option key={type.LivestockTypeID} value={type.LivestockTypeID}>
                {type.LivestockTypeName}
              </option>
            ))}
          </Select>
        )}
      </FormControl>
      {chartData.length === 0 ? (
        <Text color="gray.500" textAlign="center" mt={10}>
          ไม่มีข้อมูลสำหรับแสดงกราฟตามตัวกรองที่เลือก
        </Text>
      ) : (
        <ResponsiveContainer width="100%" height={400}>
          <BarChart
            data={chartData}
            margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="date"
              tickFormatter={(value) =>
                new Date(value + "-01").toLocaleDateString("th-TH", {
                  year: "numeric",
                  month: "short",
                })
              }
            />
            <YAxis
              label={{
                value: "ปริมาณ (หน่วย)",
                angle: -90,
                position: "insideLeft",
                offset: -10,
              }}
              tickFormatter={(value) => value.toLocaleString()}
            />
            <Tooltip
              formatter={(value) => [value.toLocaleString(), "ปริมาณ"]}
              labelFormatter={(label) =>
                new Date(label + "-01").toLocaleDateString("th-TH", {
                  year: "numeric",
                  month: "long",
                })
              }
            />
            <Legend />
            {vaccineTypes.map((vaccine, index) => (
              <Bar
                key={vaccine}
                dataKey={vaccine}
                name={vaccine}
                fill={COLORS[index % COLORS.length]}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      )}
    </Box>
  );
};

export default VaccineProductionChart;